import React, {
    useContext,
    useState,
    useRef,
    useEffect,
} from 'react';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";
import * as UTILS from "../utils/utils";

import { getBasketData, refetchBasketData } from "../queries/global";
import { NotificationContext, handleError } from '../context/NotificationContext';

import TicketTypeSelect from '../components/ticket-type-select';
import CrossIcon from '../svg/cross';
import InfoCircle from "../svg/info-circle";
import CheckForMemberDelete from './check-for-member-delete';


const campaign_name_spektrix = config.EVENT?.attributes.DonationUpsellCampaignName;
const ticketDonations = config.OPTION_FIELDS.donation_campaign?.find( donation => donation.spektrix_attribute_name === campaign_name_spektrix);
const fundId = ticketDonations?.spektrix_donation_fund;

export default function SeatSelectionSummary(props) {
    const [ticketGroup, setTicketGroup] = useState(null);
    const [supplementaryGroup, setSupplementaryGroup] = useState(null);
    const [ticketsTotal, setTicketsTotal] = useState(null);
    const [donationsTotal, setDonationsTotal] = useState(null);
    const [supplementaryTotal, setSupplementaryTotal] = useState(null);
    const [total, setTotal] = useState(0);
    const [donationId, setDonationId] = useState(null);
    const [supplementaryProtectionTicketId, setSupplementaryProtectionTicketId] = useState(null)
    const [membershipSubscriptions, setMembershipSubscriptions] = useState(null);

    const { data: basketData, isFetching: basketDataFetching } = getBasketData();    

    useEffect(() => {

        if (!basketData || (!basketData?.tickets && !basketData?.membershipSubscriptions)) {
            setTicketGroup(null);
            setTicketsTotal(null);
            setSupplementaryGroup(null);
            setSupplementaryProtectionTicketId(null);
            setMembershipSubscriptions(null);
            return;
        }
      
        // To show in the continue button
        let tickets = UTILS.getTicketsByInstance(basketData.tickets);

        if (!tickets?.length && !basketData?.membershipSubscriptions) {
            setTicketGroup(null);
            setTicketsTotal(null);
            setSupplementaryGroup(null);
            setDonationsTotal(0);
            setSupplementaryProtectionTicketId(null);
            return;
        }      
      
        const grouped_tickets = UTILS.groupTicketsByInstance(tickets);
        const tickets_total = UTILS.getTicketsTotal(tickets);
        const donation_in_basket = basketData.donations?.find((donation) => donation.fund?.id == fundId);
        const donations_total = donation_in_basket?.amount || 0;

        const supplementary =  config.SUPPLEMENTARY_INSTANCES?.map(instance => UTILS.getTicketsByInstance(basketData.tickets, instance)).filter(arr => arr.length) ?? [];
        const supplementary_group = supplementary.map( supplementary => UTILS.groupTicketsByInstance(supplementary)).flat();
        const supplementary_total = supplementary_group.reduce((total, event) => total + (event.tickets_total || 0), 0);
        const membership_total = basketData?.membershipSubscriptions.reduce((total, membership) => total + (membership.price || 0), 0);

        setTicketGroup(grouped_tickets[0]);
        setTicketsTotal(tickets_total.toFixed(2));
        setDonationsTotal(donations_total);
        setTotal(tickets_total + donations_total + supplementary_total + membership_total);
        setSupplementaryGroup(supplementary_group)
        setSupplementaryTotal(supplementary_total)

        if(supplementary_group.length && tickets.length) {
            const protection_id = UTILS.getSupplementaryProtectionId(basketData.tickets);
            const protection_ticket_id = UTILS.getTicketsByInstance(basketData.tickets, protection_id)[0].id;
            setSupplementaryProtectionTicketId(protection_ticket_id);
        }

        if (donation_in_basket) {
            setDonationId(donation_in_basket.id);
        }

        setMembershipSubscriptions(basketData?.membershipSubscriptions)
        
    }, [basketData]);

// const removeMembership = useMutation({
//         mutationFn: (membershipId) => QUERIES.removeMembership(membershipId),
//         onError: (error) => {
//             handleError(error.message, setNotification);
//         },
//         onSuccess: () => {
//             refetchBasketData();
//         },
//     });
// removeMembership.mutateAsync(basketData?.membershipSubscriptions[0].id)

    return (
        <div className='basket-summary'>
            <h2 className="booking-pathway__header--aside">Basket</h2>
            {(ticketGroup?.tickets.length > 0 || donationsTotal > 0 || supplementaryGroup?.length || membershipSubscriptions?.length) ?
                <ul role="list" className="basket-summary__items basket-summary__items--seats">
                    {ticketGroup && ticketGroup.seats.map((ticket) => (
                        <li key={ticket.ticket.id} className='basket-summary__item'>
                            <SeatSelectionSummaryItem ticket={ticket} supplementaryProtectionTicketId={supplementaryProtectionTicketId} showType = {props.showType} />
                        </li>
                    ))}
                    {donationsTotal > 0 && 
                        <li className='basket-summary__item'>
                            <DonationSummaryItem donationsTotal={donationsTotal} donationId={donationId} />
                        </li>
                    }
                    {supplementaryGroup && supplementaryGroup.map((supplementary) => (
                        <li key={supplementary.instance_id} className='basket-summary__item'>
                            <SupplementarySummaryItem supplementaryGroup={supplementary} />
                        </li>
                    ))}

                    {membershipSubscriptions && membershipSubscriptions.map((membership => (
                        <li className='basket-summary__item'>
                            <MembershipSubscriptionsItem key={membership.id} membership={membership} setCheckedForPriority={props.setCheckedForPriority} setHoldMembership={props.setHoldMembership} />
                        </li>
                    )))}


                    <li className='basket-summary__item basket-summary__total'>
                        <TotalItem total={total} />
                    </li>

                </ul>
                : <p>Your seat selection will appear here.</p>
            }


                {config.OPTION_FIELDS.concession_message ?
                    <p className='booking-pathway__info-text'>{config.OPTION_FIELDS.concession_message}</p>
            :null}
        </div>
    )
}

export const TotalItem = ({total}) => {
    return (
        <>
            <div className='basket-summary__item--primary'>
                <span className='basket-summary__item--plan-name'>
                    Sub-total
                </span>
                <span className='basket-summary__item--total'>
                    £{total.toFixed(2)}
                </span>
            </div>
        </>
    )
}

const SupplementarySummaryItem = ({supplementaryGroup}) => {
    const {notification, setNotification} = useContext(NotificationContext);
    const removeTicket = useMutation({
            mutationFn: (ticketsIdArray) => QUERIES.removeTickets(ticketsIdArray),
            onError: (error) => {
                handleError(error.message, setNotification);
            },
            onSuccess: () => {
                refetchBasketData();
            },
    })

    const handleRemoveSupplementary = () => {
        const ticketsIdArray = supplementaryGroup.tickets.map(ticket => ticket.id)
        removeTicket.mutateAsync(ticketsIdArray);
    }

    return (
        <>
            <div className='basket-summary__item--primary basket-summary__item--supplementary'>
                <p className='basket-summary__item--primary-info'>
                    <span className='basket-summary__item--plan-name'>
                        {
                            supplementaryGroup.event_name.toLowerCase().includes("protection") ?
                                "Ticket protection"
                                : supplementaryGroup.event_name
                        }
                        
                    </span>
                    <span>
                        {supplementaryGroup.tickets.length} @ £{(supplementaryGroup.tickets_total/supplementaryGroup.tickets.length).toFixed(2)}
                    </span>
                </p>
                <button type="button" disabled={removeTicket.isPending} onClick={handleRemoveSupplementary} className='btn btn--remove'>
                    <span className='sr-only'>Remove {supplementaryGroup.event_name}</span>
                    <CrossIcon />
                </button>
            </div>
        </>
    )
}

const DonationSummaryItem = ({donationsTotal, donationId}) => {
    const {notification, setNotification} = useContext(NotificationContext);

    const removeDonation = useMutation({
        mutationFn: () => QUERIES.removeDonation([donationId]),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            refetchBasketData();
        },
    });

    const handleRemoveDonation = () => {
        removeDonation.mutateAsync(donationId);
    }

    return (
        <>
            <div className='basket-summary__item--primary basket-summary__item--donation'>
                <p className='basket-summary__item--primary-info'>
                    <span className='basket-summary__item--plan-name'>
                        Donation
                    </span>
                    <span>
                        £{donationsTotal.toFixed(2)}
                    </span>
                </p>
                <button type="button" onClick={handleRemoveDonation} className='btn btn--remove'>
                    <span className='sr-only'>Remove donation</span>
                    <CrossIcon />
                </button>
            </div>
        </>
    )
}

export const SeatSelectionSummaryItem = ({ticket, supplementaryProtectionTicketId, showType = false}) => {
    const {notification, setNotification} = useContext(NotificationContext);
    // Adding/Remove tickets
    const pendingSeatMutationsRef = useRef(0);
    const queryClient = useQueryClient();

    const [seatInfo, setSeatInfo] = useState(null);
    const [inEditMode, setInEditMode] = useState(false);
    const [seatTicketIsPending, setSeatTicketIsPending] = useState(null);

    const dialogRef = useRef(null);
           
    const { data: seatStatusData } = useQuery({
        queryKey: ['seatStatusData', config.INSTANCE_ID],
        queryFn: QUERIES.getSeatData,
        retry: false
    });
    
    const { 
        data: priceListData
    } = useQuery({
        queryKey: ['priceListData', ticket.ticket.instance.id],
        queryFn: () => QUERIES.getInstancePriceList(ticket.ticket.instance.id),
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 86400000,
        enabled: false,
        onError: (error) => {
            handleError(error.message, setNotification);
        },
    });

    // Don't refetch basket and details data until add/remove function are completed
    const trackMutation = async (mutationFn) => {
        setSeatTicketIsPending(true);
        pendingSeatMutationsRef.current += 1;

        try {
        await mutationFn();
        } catch (e) {
        // Error already handled in onError
        } finally {
        pendingSeatMutationsRef.current -= 1;

        if (pendingSeatMutationsRef.current === 0) {
            refetchBasketData();
            queryClient.invalidateQueries({
            queryKey: ["seatStatusData", config.INSTANCE_ID],
            exact: true,
            });
            setSeatTicketIsPending(false);
        }
        }
    };

    const removeTicket = useMutation({
        mutationFn: (seatBasketArray) => QUERIES.removeTickets(seatBasketArray),
        onError: (error) => handleError(error.message, setNotification),
    });

    const handleRemoveTicket = (seatBasketId) => {
        const seatBasketArray = [seatBasketId];

        if(supplementaryProtectionTicketId) {
            seatBasketArray.push(supplementaryProtectionTicketId)
        };

        trackMutation(() => removeTicket.mutateAsync(seatBasketArray));
    };

    useEffect(() => {
        if (!seatStatusData?.info || !ticket?.seat) return;

        // Find out if there is extra info
        if (seatStatusData.info[ticket.seat.id]) {
            setSeatInfo(seatStatusData.info[ticket.seat.id]);
        }
    }, [seatStatusData]);

    return (
        <>
            <div className='basket-summary__item--primary'>
                <p className='basket-summary__item--primary-info'>
                    {ticket.plan_name && (
                        <span className='basket-summary__item--plan-name'>
                            {ticket.plan_name}
                        </span>
                    )}
                    {ticket.seat?.name && (
                        <span className='basket-summary__item--seat-name'>
                            {ticket.seat.name}
                        </span>
                    )}
                </p>
                
                
            </div>
            

            <div className='basket-summary__item--secondary'>
                {ticket.ticket.ticketType?.name && (
                    <p className='basket-summary__item--ticket-type'>
                        {inEditMode || showType ? 
                            <TicketTypeSelect ticket={ticket.ticket} pricelist={priceListData} setInEditMode={setInEditMode} /> 
                            : ticket.ticket.ticketType?.name &&
                                <span className='basket-summary__item--ticket-type-info'>{ticket.ticket.ticketType.name}: £{ticket.ticket.total.toFixed(2)}</span>
                        }

                        { !showType && (
                            <button type="button" onClick={() => setInEditMode(!inEditMode)} className='basket-summary__item--ticket-type-button'>                        
                                <span className='sr-only'>{inEditMode ? 'Change ticket type' : 'Edit ticket type'}</span>
                                {inEditMode ? 'Done': 'Edit'}
                            </button> 
                        )}
                    </p>
                )}

                <button type="button" disabled={removeTicket.isPending} onClick={() => handleRemoveTicket(ticket.ticket.id)} className='btn btn--remove'>
                    <span className='sr-only'>Remove ticket</span>
                    <CrossIcon />
                </button>
            </div>

            { seatInfo &&
                <div className="basket-summary__item--seat-info">
                    <InfoCircle /> <p>{decodeURIComponent(seatInfo.priceBandId.replaceAll("+"," "))}</p>
                </div>
            }

            

            {ticket.seat?.attribute_ViewFromSeatImage ?
                <button type="button" 
                        className='basket-summary__item--view-button'
                        onClick={() => dialogRef.current.showModal()}>
                    View from seat
                </button>
            :null}

            {ticket.seat?.attribute_ViewFromSeatImage ?
                <dialog ref={dialogRef} className='seat-info-dialog'>
                    <div className='dialog-container'>
                        <button type="button" className='btn--cross btn--dialog-close' onClick={() => dialogRef.current.close()}>
                            <span className='sr-only'>Close dialog</span>
                            <CrossIcon />
                        </button>
                        <div className='dialog-content'>

                            <h1 className='seat-info-dialog__header'>{ ticket.plan_name }</h1>
                            {ticket.seat.name &&
                                <p className='seat-info-dialog__seat-name'>{ ticket.seat.name }</p>
                            }
                            {ticket.seat.attribute_ViewFromSeatImage &&
                                <>
                                    <img src={ticket.seat.attribute_ViewFromSeatImage} alt="image of view from seat" className='seat-info-dialog__image' />
                                    <p className='seat-info-dialog__image--info'>Images are illustrative of approximate views</p>
                                </>
                            }
                        </div>
                    </div>
                </dialog>
            :null}
        </>
    )
}

const MembershipSubscriptionsItem = ({membership, setCheckedForPriority}) => {
    const [showModal, setShowModal] = useState(false);
    
    const removeItem = () => {
        setShowModal(true);
    }

    return (
        <>
            <div className='basket-summary__item--primary basket-summary__item--membership'>
                <p className='basket-summary__item--primary-info'>
                    <span className='basket-summary__item--plan-name'>
                        {membership.membership.name}
                    </span>
                    <span>
                        £{membership.membership.price.toFixed(2)}
                    </span>
                </p>
                <button type="button" onClick={() => removeItem()}  className='btn btn--remove'>
                    <span className='sr-only'>Remove membership</span>
                    <CrossIcon />
                </button>
            </div>
            {showModal &&
                <CheckForMemberDelete membershipId={membership.id} setCheckedForPriority={setCheckedForPriority} setShowModal={setShowModal} />                
            }
        </>
    )
}